import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ChevronLeft, ChevronRight, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stable motion configs — defined outside the component so they aren't
// recreated every render (prevents framer-motion from treating every
// render as a prop change and re-triggering the enter animation).
const MOCKUP_IMG_INITIAL = { opacity: 0, scale: 1.02 };
const MOCKUP_IMG_ANIMATE = { opacity: 1, scale: 1 };
const MOCKUP_IMG_EXIT = { opacity: 0 };
const MOCKUP_IMG_TRANSITION = { duration: 0.4 };

/**
 * Public share landing page for a single order's AI-generated mockups.
 *
 * Anyone with the share token URL can see the watermarked mockups plus a
 * CTA to "Make your own" — converting every share into a marketing surface
 * for Evergreen Graphics. No auth required; no private order data exposed.
 */
export default function PublicMockupPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/public/mockup/${token}/info`);
        if (alive) setInfo(r.data);
      } catch (err) {
        const msg = err?.response?.data?.detail || 'This share link is invalid or expired.';
        if (alive) setError(typeof msg === 'string' ? msg : 'Could not load mockup.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const filenames = useMemo(() => info?.filenames || [], [info]);
  const currentUrl = useMemo(() => {
    if (!filenames.length) return null;
    return `${API_URL}/api/public/mockup/${token}/image/${encodeURIComponent(filenames[index])}`;
  }, [filenames, index, token]);

  const cycle = (dir) => {
    if (!filenames.length) return;
    setIndex((prev) => (prev + dir + filenames.length) % filenames.length);
  };

  const productTitle = useMemo(() => {
    if (!info) return '';
    const mat = (info.material || '').trim();
    const pt = (info.product_type || '').trim();
    // Guard against junk single-char product_type values (e.g., seed data
    // with 'X') — fall back to a clean default so the H1 never shows 'X'.
    const ptClean = pt.length >= 3 ? pt : '';
    if (mat && ptClean) return `${mat} ${ptClean}`;
    return ptClean || 'Custom sticker';
  }, [info]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f1511] via-[#1a2420] to-[#0f1511] text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <header className="flex items-center justify-between mb-10">
          <Link to="/" className="text-lg font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-300" />
            {info?.brand_name || 'Evergreen Graphics'}
          </Link>
          <Link
            to="/quote"
            data-testid="public-cta-header-btn"
            className="text-xs font-semibold bg-amber-300 hover:bg-amber-400 text-amber-950 px-3 py-1.5 rounded-full transition-colors"
          >
            Make your own →
          </Link>
        </header>

        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-white/60">
            <Loader2 className="w-8 h-8 animate-spin mb-2" />
            <p className="text-sm">Loading mockup…</p>
          </div>
        )}

        {error && !loading && (
          <div
            data-testid="public-mockup-error"
            className="max-w-md mx-auto bg-red-900/40 border border-red-700/60 rounded-lg p-6 text-center"
          >
            <p className="text-sm font-semibold">{error}</p>
            <Link to="/" className="inline-block mt-4 text-amber-300 text-xs underline">
              Back to home
            </Link>
          </div>
        )}

        {info && !loading && (
          <div data-testid="public-mockup-view">
            <div className="text-center mb-6">
              <p className="text-xs uppercase tracking-[0.25em] text-amber-300 mb-2">
                Custom sticker mockup
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold">{productTitle}</h1>
              <p className="text-sm text-white/60 mt-2">
                AI-generated mockup · {filenames.length} variation{filenames.length === 1 ? '' : 's'}
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentUrl}
                  src={currentUrl}
                  alt={`${productTitle} mockup`}
                  initial={MOCKUP_IMG_INITIAL}
                  animate={MOCKUP_IMG_ANIMATE}
                  exit={MOCKUP_IMG_EXIT}
                  transition={MOCKUP_IMG_TRANSITION}
                  className="w-full h-auto block"
                  data-testid="public-mockup-image"
                />
              </AnimatePresence>
              {filenames.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => cycle(-1)}
                    data-testid="public-mockup-prev-btn"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center"
                    aria-label="Previous"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => cycle(1)}
                    data-testid="public-mockup-next-btn"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center"
                    aria-label="Next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {filenames.map((fn, i) => (
                      <button
                        key={fn}
                        type="button"
                        onClick={() => setIndex(i)}
                        aria-label={`Go to mockup ${i + 1}`}
                        className={`w-2 h-2 rounded-full transition-all ${
                          i === index ? 'bg-amber-300 scale-125' : 'bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="mt-8 text-center">
              <p className="text-white/70 text-sm mb-4">
                Love this? Design yours in under two minutes.
              </p>
              <Link
                to="/quote"
                data-testid="public-cta-primary-btn"
                className="inline-flex items-center gap-2 bg-amber-300 hover:bg-amber-400 text-amber-950 font-semibold px-6 py-3 rounded-full text-sm transition-all hover:scale-105"
              >
                Start my custom sticker
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <footer className="mt-12 text-center text-xs text-white/40">
              Mockup generated by{' '}
              <a
                href={`https://${info.brand_url || 'evergreengraphics.ca'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-amber-300"
              >
                {info.brand_name || 'Evergreen Graphics'}
              </a>
              {' · '} Die-cut stickers, labels, decals, magnets, and more.
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}
